import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import Icon from '@/components/AppIcon';
import EmployeeAvatar from '@/components/employees/EmployeeAvatar';
import useClickOutside from '@/hooks/useClickOutside';

const ensureString = (value) => (typeof value === 'string' ? value : '');

const Autocomplete = ({
  value,
  onChange,
  options = [],
  onSelect,
  placeholder = 'Search…',
  disabled = false,
  loading = false,
  emptyText = 'No matches found',
  getOptionLabel = (option) => ensureString(option?.label ?? option?.name ?? option?.title),
  getOptionDescription = (option) => ensureString(option?.description ?? option?.subtitle ?? option?.email),
  getOptionKey = (option, index) => option?.id ?? option?._id ?? index,
}) => {
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const normalizedValue = ensureString(value);

  const filteredOptions = useMemo(() => {
    if (!normalizedValue.trim()) {
      return options;
    }

    const query = normalizedValue.trim().toLowerCase();
    return options.filter((option) => {
      const label = getOptionLabel(option)?.toLowerCase?.() ?? '';
      const description = getOptionDescription(option)?.toLowerCase?.() ?? '';
      const searchText = (() => {
        if (typeof option?.searchText === 'string') {
          return option.searchText.toLowerCase();
        }

        if (Array.isArray(option?.searchTerms)) {
          return option.searchTerms
            .map((term) => String(term ?? '').toLowerCase())
            .filter((term) => term && term !== '[object object]')
            .join(' ');
        }

        return '';
      })();

      return (
        label.includes(query)
        || description.includes(query)
        || searchText.includes(query)
      );
    });
  }, [normalizedValue, options, getOptionLabel, getOptionDescription]);

  useEffect(() => {
    if (highlightedIndex >= filteredOptions.length) {
      setHighlightedIndex(0);
    }
  }, [filteredOptions, highlightedIndex]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setHighlightedIndex(0);
  }, []);

  const handleSelect = useCallback((option) => {
    if (!option || disabled) {
      return;
    }
    onSelect?.(option);
    closeDropdown();
  }, [onSelect, closeDropdown, disabled]);

  useClickOutside(containerRef, closeDropdown, isOpen);

  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  const handleKeyDown = (event) => {
    if (!isOpen && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      setIsOpen(true);
      return;
    }

    if (!filteredOptions.length) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex((prev) => (prev + 1) % filteredOptions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((prev) => (prev - 1 + filteredOptions.length) % filteredOptions.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const option = filteredOptions[highlightedIndex];
      if (option) {
        handleSelect(option);
      }
    } else if (event.key === 'Escape') {
      closeDropdown();
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Icon
          name="Search"
          size={16}
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
        />
        <input
          ref={inputRef}
          type="text"
          value={normalizedValue}
          onChange={(event) => onChange?.(event?.target?.value ?? '')}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full pl-10 pr-10 py-2 bg-input border border-border rounded-lg text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-70"
        />
        {loading && (
          <Icon
            name="Loader2"
            size={16}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground animate-spin"
          />
        )}
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-50 mt-2 w-full bg-popover border border-border rounded-lg shadow-elevation-2 max-h-64 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
              Searching…
            </div>
          ) : filteredOptions.length ? (
            filteredOptions.map((option, index) => {
              const isHighlighted = index === highlightedIndex;
              const label = getOptionLabel(option);
              const description = getOptionDescription(option);

              return (
                <button
                  type="button"
                  key={getOptionKey(option, index)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => handleSelect(option)}
                  className={`w-full text-left px-4 py-2 flex items-center space-x-3 ${
                    isHighlighted ? 'bg-muted' : 'hover:bg-muted'
                  }`}
                >
                  <EmployeeAvatar
                    url={option?.avatarUrl ?? option?.avatar}
                    name={label}
                    alt={`${label ?? 'Option'} avatar`}
                    size="sm"
                    showStatus={false}
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground truncate">{label || 'Unnamed option'}</p>
                    {description && (
                      <p className="text-xs text-muted-foreground truncate">{description}</p>
                    )}
                  </div>
                </button>
              );
            })
          ) : (
            <div className="py-6 px-4 text-sm text-muted-foreground text-center">
              {emptyText}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Autocomplete;
